export const DATA_RETENTION_POLICY = {
  id: 'data-retention-policy',
  title: 'Data Retention Policy',
  version: 'v1.0-2026-05-17',
  effectiveDate: '17/05/2026',
  audience: 'all',
  content: `DATA RETENTION POLICY — RESERVO

Status: DRAFT — pending solicitor review

1. PURPOSE

This policy defines how long Reservo retains personal information and operational records, and when and how that information is deleted or de-identified.

2. LEGAL BASIS

2.1 Retention periods are set with reference to:
  (a) Privacy Act 1988 (Cth) and Australian Privacy Principle 11;
  (b) Point to Point Transport (Taxis and Hire Vehicles) Act 2016 (NSW) record-keeping requirements;
  (c) Taxation Administration Act 1953 (Cth) and Australian Tax Office record-keeping requirements;
  (d) Corporations Act 2001 (Cth);
  (e) Limitation Act 1969 (NSW) and civil-claim limitation periods;
  (f) Anti-Discrimination Act 1977 (NSW) and analogous complaint limitation periods;
  (g) Notifiable Data Breaches scheme requirements.

3. RETENTION SCHEDULE

| Category | Period | Reason |
| --- | --- | --- |
| Account profile (Riders & Drivers) | Active account + 7 years post-closure | ATO, audit, civil claims |
| Trip records (GPS, fare, route, parties) | 7 years from trip date | NSW Point to Point Transport regulations, ATO |
| Booking metadata | 7 years from booking date | ATO |
| Payment records and tax invoices | 7 years from transaction | ATO |
| NSW Passenger Service Levy reports | 7 years | NSW Point to Point Transport Commissioner |
| Incident reports | 7 years from incident, longer if litigation pending | NSW Point to Point Transport regulations |
| Complaint records | 7 years from complaint, longer if litigation pending | Anti-Discrimination Act, audit |
| Driver authorisation evidence | Active + 7 years post-termination | NSW Point to Point Transport regulations |
| Vehicle authorisation evidence | While vehicle authorised + 7 years | NSW Point to Point Transport regulations |
| Training records | 7 years from training | NSW Point to Point Transport regulations |
| Consent records | 7 years from consent or account closure (later) | Privacy Act, audit |
| Audit log | 7 years | Audit, regulatory cooperation |
| SOS event records | 7 years | Safety, regulatory cooperation |
| Lost item records | 7 years from case closure | NSW Point to Point Transport, civil claims |
| Damage claim records | 7 years from case closure | NSW Point to Point Transport, civil claims |
| Photos linked to incidents, claims, lost items | 7 years from case closure | Evidence retention |
| Driver fatigue records | 7 years | NSW Point to Point Transport regulations |
| Marketing consent records | 7 years from withdrawal | Privacy Act, anti-spam |
| Cyber incident records | 7 years | Notifiable Data Breaches scheme |
| Support tickets | 7 years | Customer service, audit |
| Background check records | Active + 7 years post-termination | NSW Point to Point Transport regulations |

3.1 Where a record falls within multiple categories the longest period applies.

3.2 Where a record is subject to a legal hold (active litigation, regulatory investigation), it is retained until the hold is released, regardless of the schedule above.

4. METHODS OF DELETION OR DE-IDENTIFICATION

4.1 At end of retention, records are either:
  (a) deleted (hard delete from production systems and backups within a reasonable backup-rotation cycle); or
  (b) de-identified by removing personal identifiers and retaining only aggregated or pseudonymised data for analytical purposes.

4.2 Storage Tier-Down: records older than 2 years may be moved from primary storage to archival storage with reduced access controls but unchanged retention period.

4.3 Deletion of payment records is coordinated with Stripe, which retains its own records per its own retention policy.

5. USER REQUESTS

5.1 Users may request:
  (a) a copy of their personal information held by Reservo;
  (b) correction of inaccurate information;
  (c) deletion of their account.

5.2 On deletion request:
  (a) account login is disabled immediately;
  (b) personal-identifier fields are scrambled or removed once retention obligations for other categories no longer apply;
  (c) trip, incident, complaint, tax, and audit records are retained per the schedule above, with personal identifiers replaced by pseudonymised IDs where practicable;
  (d) the user is notified of the result.

5.3 Reservo responds to deletion requests within 30 days.

6. PRACTICAL IMPLEMENTATION

6.1 Reservo operates automated retention review at least annually:
  (a) identifying records past their retention date;
  (b) flagging records for deletion or de-identification;
  (c) executing approved deletion under change-control;
  (d) recording the outcome in the audit log.

6.2 Backup retention is configured to be consistent with the primary-data retention schedule, with a 90-day grace period for backup rotation.

7. CROSS-BORDER STORAGE

7.1 Primary data is stored in the Google Cloud australia-southeast1 (Sydney) region. Some metadata may be processed in other Google Cloud regions as part of provisioning, debugging, or platform operations. Reservo takes reasonable steps to ensure overseas processing complies with the APPs.

8. CONTACT

David Williams, Privacy Officer
Email: admin@reservo.au
Phone: 0435 679 630

---
VERSION HISTORY
v1.0 — 17/05/2026 — Initial draft for solicitor review.

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW.`,
};